import React from "react";
import PropTypes from "prop-types";
import { CustomScrollTable } from "../../CustomScrollTable";
import { OrdersEmptyState } from "../OrdersEmptyState";
import { useTranslation } from "@helpers/translate";
import { formatOrderDate } from "@helpers/formatOrders";
import { BADGE_ICONS, COIN_ICONS } from "./communityMockData";
import { getClickableProps } from "./rowProps";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import "./CommunityMobileList.sass";

// Cards that open the trade details or the trader profile
const CLICKABLE_CLASS = "community-mobile-clickable";

const getToneClass = (value, prefix = "leaderboard-card") => {
  const str = String(value ?? "");
  if (str.startsWith("-")) return `${prefix}__value--negative`;
  return `${prefix}__value--positive`;
};

export const CommunityMobileList = ({
  data = [],
  loading = false,
  type = "default",
  onItemClick,
}) => {
  const { __ } = useTranslation();

  if (!loading && (!data || data.length === 0)) {
    return <OrdersEmptyState className="orders-empty-state--fill" />;
  }

  const formatTotalValue = (wagerStr, profitLossStr) => {
    const parseNum = (str) => {
      if (!str) return 0;
      const clean = String(str).replace(/[$,\s]/g, "");
      const num = parseFloat(clean);
      return Number.isNaN(num) ? 0 : num;
    };

    const sum = parseNum(wagerStr) + parseNum(profitLossStr);

    return `$${sum.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // 5 карток (~72–74px) + 4 gap по 8px ≈ 392–402
  const isCardList =
    type === "public-feed" || type === "leaderboard" || type === "best-trades";
  const listMaxHeight = isCardList ? 402 : 344;
  const listClassName = isCardList ? "trade-list" : "";

  return (
    <div
      className={`community-mobile-list${
        isCardList ? ` community-mobile-list--${type}` : ""
      }`}
    >
      <CustomScrollTable
        isTable={false}
        maxHeight={listMaxHeight}
        className={listClassName}
      >
        {data.map((item, index) => {
          const itemId = item.id ?? item.rank ?? index + 1;

          if (type === "public-feed") {
            const isProfit = String(item.profitLoss).startsWith("+");
            const isLoss = String(item.profitLoss).startsWith("-");
            const toneClass = isProfit
              ? "trade-card--positive"
              : isLoss
                ? "trade-card--negative"
                : "";
            const isUp = item.trade?.direction === "up";
            const coinName = item.trade?.coin || "BTC";
            const coinIcon =
              COIN_ICONS[item.trade?.coinIcon] ||
              COIN_ICONS[coinName] ||
              null;
            const totalValue = formatTotalValue(item.wager, item.profitLoss);

            return (
              <article
                key={itemId}
                {...getClickableProps(
                  onItemClick,
                  item,
                  ["trade-card", toneClass],
                  CLICKABLE_CLASS,
                )}
              >
                <div className="trade-card__left">
                  <div className="trade-card__icon-wrap">
                    {coinIcon ? (
                      <img
                        src={coinIcon}
                        alt={coinName}
                        className="trade-card__coin-logo"
                      />
                    ) : (
                      <div className="trade-card__coin-logo trade-card__coin-logo--placeholder" />
                    )}
                    <span className="trade-card__badge">
                      {isUp ? (
                        <TradeArrowUpIcon size={7} color="#2FA766" />
                      ) : (
                        <TradeArrowDownIcon size={7} color="#E2464A" />
                      )}
                    </span>
                  </div>

                  <div className="trade-card__info">
                    <div className="trade-card__row trade-card__row--symbol">
                      <span className="trade-card__ticker">{coinName}</span>
                      <span className="trade-card__price">{item.entry}</span>
                    </div>

                    <div className="trade-card__row trade-card__row--size">
                      <span className="trade-card__wager">{item.wager}</span>
                      <span className="trade-card__leverage">
                        {item.leverage}
                      </span>
                    </div>

                    <div className="trade-card__row trade-card__row--user">
                      <span className="trade-card__username">
                        {item.username}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="trade-card__stats">
                  <span
                    className={`trade-card__multiplier ${
                      isProfit
                        ? "trade-card__value--positive"
                        : isLoss
                          ? "trade-card__value--negative"
                          : ""
                    }`}
                  >
                    {item.multiplier}
                  </span>
                  <span
                    className={`trade-card__pnl ${
                      isProfit
                        ? "trade-card__value--positive"
                        : isLoss
                          ? "trade-card__value--negative"
                          : ""
                    }`}
                  >
                    {item.profitLoss}
                  </span>
                  <span className="trade-card__total">{totalValue}</span>
                </div>
              </article>
            );
          }

          if (type === "leaderboard") {
            const badgeSrc = BADGE_ICONS[item.badgeIcon];
            const roiTone = getToneClass(item.totalRoi);
            const profitTone = getToneClass(item.totalProfit);
            const winTone = getToneClass(item.biggestWin);

            return (
              <article
                key={itemId}
                {...getClickableProps(
                  onItemClick,
                  item,
                  "leaderboard-card",
                  CLICKABLE_CLASS,
                )}
              >
                <div className="leaderboard-card__left">
                  <div className="leaderboard-card__badge-wrap">
                    {badgeSrc ? (
                      <img
                        src={badgeSrc}
                        alt={`rank ${item.rank}`}
                        className="leaderboard-card__badge"
                      />
                    ) : (
                      <span className="leaderboard-card__rank-num">
                        {item.rank}
                      </span>
                    )}
                  </div>

                  <div className="leaderboard-card__info">
                    <span className="leaderboard-card__username">
                      {item.user}
                    </span>
                    <span className="leaderboard-card__label">
                      {__("futures.community.total_profit")}
                    </span>
                    <span className="leaderboard-card__label">
                      {__("futures.community.biggest_win")}
                    </span>
                  </div>
                </div>

                <div className="leaderboard-card__stats">
                  <span className={`leaderboard-card__roi ${roiTone}`}>
                    {item.totalRoi}
                  </span>
                  <span className={`leaderboard-card__profit ${profitTone}`}>
                    {item.totalProfit}
                  </span>
                  <span className={`leaderboard-card__win ${winTone}`}>
                    {item.biggestWin}
                  </span>
                </div>
              </article>
            );
          }

          if (type === "best-trades") {
            const badgeSrc = BADGE_ICONS[item.badgeIcon];
            const coinName = item.trade?.symbol || "BTC";
            const coinIcon =
              COIN_ICONS[item.trade?.assetIcon] ||
              COIN_ICONS[coinName] ||
              null;
            const multiplierTone = getToneClass(
              item.payoutMultiplierIncludingFee,
              "best-trade-card",
            );
            const profitTone = getToneClass(item.profit, "best-trade-card");

            return (
              <article
                key={itemId}
                {...getClickableProps(
                  onItemClick,
                  item,
                  "best-trade-card",
                  CLICKABLE_CLASS,
                )}
              >
                <div className="best-trade-card__left">
                  <div className="best-trade-card__badge-wrap">
                    {badgeSrc ? (
                      <img
                        src={badgeSrc}
                        alt={`rank ${item.rank}`}
                        className="best-trade-card__badge"
                      />
                    ) : (
                      <span className="best-trade-card__rank-num">
                        {item.rank}
                      </span>
                    )}
                  </div>

                  <div className="best-trade-card__info">
                    <div className="best-trade-card__row best-trade-card__row--asset">
                      {coinIcon ? (
                        <img
                          src={coinIcon}
                          alt={coinName}
                          className="best-trade-card__coin-logo"
                        />
                      ) : (
                        <div className="best-trade-card__coin-logo best-trade-card__coin-logo--placeholder" />
                      )}
                      <span className="best-trade-card__ticker">
                        {coinName}
                      </span>
                    </div>

                    <div className="best-trade-card__row best-trade-card__row--size">
                      <span className="best-trade-card__wager">
                        {item.wager}
                      </span>
                      <span className="best-trade-card__leverage">
                        {item.leverage}
                      </span>
                    </div>

                    <span className="best-trade-card__username">
                      {item.user}
                    </span>
                  </div>
                </div>

                <div className="best-trade-card__stats">
                  <span
                    className={`best-trade-card__multiplier ${multiplierTone}`}
                  >
                    {item.payoutMultiplierIncludingFee}
                  </span>
                  <span className={`best-trade-card__profit ${profitTone}`}>
                    {item.profit}
                  </span>
                  <span className="best-trade-card__date">
                    {formatOrderDate(item.created_at) ?? "—"}
                  </span>
                </div>
              </article>
            );
          }

          return (
            <div key={itemId} className="community-mobile-item">
              <span className="community-mobile-item__label">ID:</span>
              <span className="community-mobile-item__value">{itemId}</span>
            </div>
          );
        })}
      </CustomScrollTable>
    </div>
  );
};

CommunityMobileList.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  type: PropTypes.string,
  onItemClick: PropTypes.func,
};

export default CommunityMobileList;
